import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FaFacebookF, FaInstagram, FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate footer elements on scroll
      gsap.fromTo(".footer-heading",
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".footer-heading",
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

      gsap.fromTo(".footer-item",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.2,
          scrollTrigger: {
            trigger: ".footer-item",
            start: "top 85%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Floating animation for CTA button
      gsap.to(".footer-cta", {
        y: -10,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

    }, footerRef);

    return () => ctx.revert();
  }, []);

  const quickLinks = [
    { name: "Gallery", path: "/gallery" },
    { name: "Services", path: "/services" },
    { name: "Catalog", path: "/catalog" },
    { name: "Contact", path: "/contact" },
    { name: "Customer Satisfaction", path: "/testimonials" }
  ];

  const services = [
    "Custom Kitchen Design",
    "Cabinet Installation",
    "Countertop Services",
    "Kitchen Remodeling",
    "3D Visualization",
    "Free Consultation"
  ];

  const socialLinks = [
    { 
      icon: <FaFacebookF size={18} />, 
      href: "https://www.facebook.com/laminancecabinetry", 
      color: "hover:text-blue-600",
      label: "Facebook"
    },
    { 
      icon: <FaInstagram size={18} />, 
      href: "https://www.instagram.com/laminancecabinetry/", 
      color: "hover:text-pink-600",
      label: "Instagram"
    }
  ];

  return (
    <footer ref={footerRef} className="bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>

      {/* Animated Orbs */}
      <div className="absolute top-10 left-10 w-4 h-4 bg-orange-500 rounded-full opacity-60 animate-pulse"></div>
      <div className="absolute top-1/4 right-20 w-3 h-3 bg-amber-400 rounded-full opacity-40 animate-pulse delay-1000"></div>
      <div className="absolute bottom-1/3 left-20 w-2 h-2 bg-orange-300 rounded-full opacity-50 animate-pulse delay-500"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 relative z-10">
        {/* CTA Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 px-4 py-2 rounded-full mb-6">
            <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
            <span className="text-orange-300 text-sm font-semibold">Let's Get Started</span>
          </div>

          <h2 className="footer-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Build Your{" "}
            <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
              Dream Kitchen
            </span>{" "}
            With Us
          </h2>
          
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Transform your space with our expert design and craftsmanship. Your dream kitchen is just a conversation away.
          </p>

          <motion.div className="footer-cta">
  <Link
    to="/contact"
    className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-2xl font-bold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300 group"
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    onClick={() => window.scrollTo(0, 0)}
  >
    <span>Schedule Free Consultation</span>
    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
    </svg>
  </Link>
</motion.div>
        </motion.div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <motion.div 
            className="footer-item"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
              <img
                src="/assets/Images/logoimg.png"
                alt="Laminance - Your Kitchen Design Partner"
                className="mb-6 w-40 h-auto filter brightness-0 invert"
              />
              <p className="text-gray-300 mb-6 leading-relaxed">
                Creating beautiful, functional kitchens that transform homes and lives. 
                Your vision, our expertise - perfect harmony.
              </p>
              
              {/* Social Links */}
              <div className="flex space-x-4">
                {socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-gray-300 ${social.color} backdrop-blur-sm border border-white/10 hover:border-orange-500/30 transition-all duration-300`}
                    whileHover={{ scale: 1.1, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    aria-label={social.label}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div 
            className="footer-item"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 h-full">
              <h3 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
                <FaMapMarkerAlt className="text-orange-400" />
                Get In Touch
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3 group">
                  <FaMapMarkerAlt className="text-orange-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-gray-300 group-hover:text-white transition-colors">
                      Laminance
                    </p>
                    <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">
                      418 Rt 23, Franklin, NJ, 07416
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 group">
                  <FaPhone className="text-orange-400 flex-shrink-0" />
                  <a 
                    href="tel:+18624506069" 
                    className="text-gray-300 group-hover:text-white transition-colors hover:underline"
                  >
                    (862) 450-6069
                  </a>
                </div>
                
                <div className="flex items-center gap-3 group">
                  <FaEnvelope className="text-orange-400 flex-shrink-0" />
                  <a 
                    href="mailto:Contactus@laminance.com" 
                    className="text-gray-300 group-hover:text-white transition-colors hover:underline"
                  >
                    Contactus@laminance.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div 
            className="footer-item"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 h-full">
              <h3 className="text-xl font-bold mb-6 text-white">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path}
                      className="text-gray-300 hover:text-orange-400 transition-colors duration-300 flex items-center gap-2 group"
                    >
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Services - Links to Premium Service Page */}
          <motion.div 
            className="footer-item"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 h-full">
              <Link to="/" className="group">
                <h3 className="text-xl font-bold mb-6 text-white flex items-center gap-2 group-hover:text-orange-400 transition-colors">
                  Our Services
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </h3>
              </Link>
              <ul className="space-y-3">
                {services.map((service, index) => (
                  <li key={index}>
                    <Link 
                      to="/"
                      className="text-gray-300 hover:text-orange-400 transition-colors duration-300 flex items-center gap-2 group cursor-pointer"
                    >
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      {service}
                    </Link>
                  </li>
                ))}
              </ul>
              
              {/* View All Services Button */}
              <motion.div className="mt-6 pt-4 border-t border-white/10">
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors text-sm font-semibold group"
                >
                  View All Premium Services
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div 
          className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Laminance Cabinetry. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm text-gray-400">
            <Link to="/" className="hover:text-orange-400 transition-colors">Privacy Policy</Link>
            <Link to="/" className="hover:text-orange-400 transition-colors">Terms of Service</Link>
            <Link to="/" className="hover:text-orange-400 transition-colors">Sitemap</Link>
          </div>
        </motion.div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none"></div>
    </footer>
  );
};

export default Footer;